import { useCallback, useRef } from 'react';
import Image from 'next/image';

import { useHitSwatches } from '@hooks/useHitSwatches';
import { useLocale } from '@hooks/useLocale';
import type { HitProps } from '@components/algolia/Hit';
import QuickAdd from '_components/QuickAdd';
import { FEATURE_FLAG_QUICK_ADD } from '@config/feature-flag';
import { canWhyChooseQuickAdd } from '@adapters/prismic/utils/canWhyChooseQuickAdd';
import {
  WHY_CHOOSE_ATC_SOURCE_BY_PAGE_TYPE,
  WHY_CHOOSE_ITEM_LIST_NAME,
  trackWhyChooseQuickAddClick,
  type WhyChoosePageType,
} from '_utils/GA4/trackWhyChooseEvents';

import styles from './QuickAddChip.module.scss';

export interface QuickAddChipProps {
  product: HitProps;
  /** The Why Choose document's co-op brand, for GA4 `coop_brand`. */
  coOpBrand: string;
  /** The page the section renders on, for GA4 attribution (ED-590). */
  pageType: WhyChoosePageType;
}

/** Quick Add analytics per page type (ED-590). The PDP keeps its ED-422
    values; the homepage, CIP and CLP report a per-page `atc_source`, reused
    as `item_list_name`, the same way the Image Hotspot does (ED-586). */
const quickAddAnalytics = (pageType: WhyChoosePageType) => {
  if (pageType === 'pdp') {
    return { itemListName: WHY_CHOOSE_ITEM_LIST_NAME };
  }

  const atcSource = WHY_CHOOSE_ATC_SOURCE_BY_PAGE_TYPE[pageType];
  return { itemListName: atcSource, atcSource };
};

/**
 * Quick add for the Why Choose featured product (ED-357): Figma's "Add to
 * Cart Button, State=With Image" (360:8984) — a product thumbnail beside a
 * cart button — pinned top-right of the section image. Composes the same
 * `QuickAdd` as `ProductTile` (same eligibility gate, colour
 * sync and member pricing via `useHitSwatches`) with `compactVariant="chip"`:
 * desktop opens Figma 867:193013's card over the chip, narrow viewports get
 * QuickAdd's usual bottom sheet.
 */
const QuickAddChip = ({ product, coOpBrand, pageType }: QuickAddChipProps) => {
  const locale = useLocale();
  const chipRef = useRef<HTMLDivElement>(null);
  const { activeVariant, swatches } = useHitSwatches(
    product.colour,
    product.variants,
    {
      image: product.image,
      imageTransparent: product.imageTransparent,
      badge: product.badge ?? null,
      badgeGuest: product.badgeGuest ?? null,
      price: product.price,
      salePrice: product.salePrice,
      sku: product.sku,
    },
    locale
  );

  // Keeps the thumbnail and the popover's summary on the colour being
  // configured — same approach as `ProductTile`.
  const handleColourChange = useCallback(
    (colourName: string) => {
      swatches
        .find(
          (swatch) =>
            swatch.swatchName.toLowerCase() === colourName.toLowerCase()
        )
        ?.onClick();
    },
    [swatches]
  );

  // ED-422: `click_why_choose_atc` replaces Quick Add's default
  // `click_quick_atc` when the trigger opens the popover.
  const handleTriggerOpen = useCallback(() => {
    trackWhyChooseQuickAddClick({
      data: {
        interactionLocation: `${window.location.pathname}${window.location.search}`,
        coOpBrand: coOpBrand || null,
        productId: product.parentSku,
        pageType,
      },
    });
  }, [coOpBrand, product.parentSku, pageType]);

  const activeColourKey = activeVariant?.swatchName ?? product.colour;

  if (!FEATURE_FLAG_QUICK_ADD || !canWhyChooseQuickAdd(product)) return null;

  const imageSrc =
    activeVariant?.imageTransparent ||
    activeVariant?.image ||
    product.imageTransparent ||
    product.image ||
    '/images/pdp/product-placeholder.jpg';
  const displayPrice =
    activeVariant?.priceRange?.price || activeVariant?.priceFormatted || '';
  const salePrice =
    activeVariant?.priceRange?.salePrice ||
    activeVariant?.salePriceFormatted ||
    undefined;

  return (
    <div ref={chipRef} className={styles.chip}>
      <span className={styles.thumbnail}>
        <Image
          src={imageSrc}
          alt=""
          fill
          // 2x the 48px box (as `StickyAddToCartBar`): an exact-fit 96w file
          // re-encoded at q75 reads soft next to the browser-downscaled
          // photography around it.
          sizes="96px"
          className={styles.thumbnailImage}
        />
      </span>
      <div className={styles.cartAction}>
        <QuickAdd
          compact
          compactVariant="chip"
          stackedAnchorRef={chipRef}
          productCode={product.parentSku}
          productTitle={product.title}
          productUrl={product.url}
          initialColour={{
            colourName: activeColourKey,
            colourSku: activeVariant?.sku ?? product.sku,
          }}
          summary={{ imageSrc, displayPrice, salePrice }}
          onColourChange={handleColourChange}
          analytics={{
            source: 'why_choose',
            ...quickAddAnalytics(pageType),
            coOpBrand: coOpBrand || null,
          }}
          onTriggerOpen={handleTriggerOpen}
        />
      </div>
    </div>
  );
};

export default QuickAddChip;
